/** How one trigger string behaves. */
export interface TriggerRule {
  trigger: string;
  /** Whether the query may contain spaces (`@Ada Lovelace`). Commands usually say no. */
  allowSpaces: boolean;
  /** Characters the user must type after the trigger before it counts. */
  minQueryLength: number;
  /** Only at the very start of a block, the way a slash command is usually offered. */
  blockStartOnly: boolean;
}

export interface TriggerMatch {
  trigger: string;
  query: string;
  /** Offset of the trigger's first character in the text that was searched. */
  start: number;
}

const MAX_QUERY_LENGTH = 64;
/** What may come right before a trigger: nothing, a space, or an opening mark. */
export const BOUNDARY: RegExp = /[\s([{"'«—–]/;

/**
 * Finds the trigger the caret is in the middle of, given the text of the current node up to the
 * caret. Pure, so every rule below has a plain unit test.
 *
 * A trigger counts only at the start of the text or after a space or opening mark, which is what
 * keeps `a@b.com`, `https://x.com/@user`, and `C#` from opening a menu. The query may not start
 * with a space, which is what keeps `# ` a heading. Of several triggers that match, the longest
 * wins, so `[[` is not mistaken for `[`.
 */
export function findTrigger(
  textBeforeCaret: string,
  rules: readonly TriggerRule[],
  atBlockStart = true,
): TriggerMatch | null {
  const ordered = rules.toSorted((a, b) => b.trigger.length - a.trigger.length);
  // The nearest trigger to the caret wins; a query never reaches back past another trigger.
  for (let start = textBeforeCaret.length - 1; start >= 0; start--) {
    if (textBeforeCaret.length - start > MAX_QUERY_LENGTH + 2) return null;
    for (const rule of ordered) {
      if (!textBeforeCaret.startsWith(rule.trigger, start)) continue;
      // The second `[` of `[[` is not a `[` trigger: a longer trigger ending here owns it, and is
      // found when the scan reaches where it begins.
      const end = start + rule.trigger.length;
      const shadowed = ordered.some(
        (longer) =>
          longer.trigger.length > rule.trigger.length &&
          end >= longer.trigger.length &&
          textBeforeCaret.startsWith(longer.trigger, end - longer.trigger.length),
      );
      if (shadowed) continue;
      if (start > 0 && !BOUNDARY.test(textBeforeCaret[start - 1]!)) continue;
      if (rule.blockStartOnly && !(atBlockStart && start === 0)) continue;
      const query = textBeforeCaret.slice(start + rule.trigger.length);
      if (query.length > MAX_QUERY_LENGTH || query.length < rule.minQueryLength) return null;
      if (/^\s/.test(query) || query.includes("\n")) return null;
      if (!rule.allowSpaces && /\s/.test(query)) return null;
      // Two spaces in a row, or a trailing sentence, means the user moved on.
      if (/\s{2}/.test(query) || query.split(/\s+/).length > 4) return null;
      // A closed wiki link is finished, not a query.
      if (rule.trigger === "[[" && query.includes("]]")) return null;
      return { trigger: rule.trigger, query, start };
    }
  }
  return null;
}
